import type { KeyboardEvent } from 'react';

import { match } from '@frontend/duck-tape';
import { useCallback } from '@frontend/react';
import { Icon, XStack } from '@frontend/web-react';
import { concatClasses } from '@frontend/web-utils';

import { PROFILE_WIZARD_STEP_CONFIGS, STEP_CIRCLE_SIZE } from './ProfileWizardCard.constants';
import type { ProfileWizardStep } from './ProfileWizardCard.types';

type ProfileWizardStepIndicatorProps = {
  completedSteps: ReadonlyArray<ProfileWizardStep>;
  currentStep: ProfileWizardStep;
  onGoToStep: (step: ProfileWizardStep) => void;
};

type StepCircleState = 'active' | 'completed' | 'pending';

const getStepState = (
  step: ProfileWizardStep,
  currentStep: ProfileWizardStep,
  completedSteps: ReadonlyArray<ProfileWizardStep>,
): StepCircleState => {
  if (completedSteps.includes(step)) return 'completed';
  if (step === currentStep) return 'active';
  return 'pending';
};

export const ProfileWizardStepIndicator = ({
  completedSteps,
  currentStep,
  onGoToStep,
}: ProfileWizardStepIndicatorProps) => (
  <XStack alignItems="center" gapX="none">
    {PROFILE_WIZARD_STEP_CONFIGS.map((config, index) => {
      const state = getStepState(config.step, currentStep, completedSteps);
      const previousConfig = PROFILE_WIZARD_STEP_CONFIGS[index - 1];
      const showLine = index > 0;
      const isPreviousCompleted = previousConfig
        ? completedSteps.includes(previousConfig.step) || previousConfig.step === currentStep
        : false;

      return (
        <XStack alignItems="center" gapX="none" key={config.step}>
          {showLine ? (
            <div
              className={concatClasses('h-[2px] w-[24px]', isPreviousCompleted ? 'bg-surfaceAccent' : 'bg-neutral-200')}
            />
          ) : null}
          <StepCircle
            onClick={state !== 'active' ? () => onGoToStep(config.step) : undefined}
            state={state}
            stepConfig={config}
          />
        </XStack>
      );
    })}
  </XStack>
);

type StepCircleProps = {
  onClick: MaybeUndefined<() => void>;
  state: StepCircleState;
  stepConfig: (typeof PROFILE_WIZARD_STEP_CONFIGS)[number];
};

const StepCircle = ({ onClick, state, stepConfig }: StepCircleProps) => {
  const isClickable = state !== 'active';
  const baseClasses = 'flex items-center justify-center rounded-full';

  const stateClasses = match(state)
    .with('active', () => 'bg-neutral-950')
    .with('completed', () => 'bg-surfaceAccent cursor-pointer')
    .with('pending', () => 'bg-neutral-200 cursor-pointer')
    .exhaustive();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if ((e.key === 'Enter' || e.key === ' ') && onClick) {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  return (
    <div
      className={concatClasses(baseClasses, stateClasses)}
      onClick={onClick}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      role={isClickable ? 'button' : undefined}
      style={{ height: STEP_CIRCLE_SIZE, width: STEP_CIRCLE_SIZE }}
      tabIndex={isClickable ? 0 : undefined}
    >
      {state === 'completed' ? (
        <Icon color="primary" name="IconCheck" size="sm" />
      ) : (
        <Icon color={state === 'active' ? 'white' : 'slate300'} name={stepConfig.icon} size="sm" />
      )}
    </div>
  );
};
